/**
 * Date utility functions for ActionPlan module
 */

/**
 * Parse ISO date string as UTC Date object WITHOUT timezone conversion
 * This ensures dates remain consistent regardless of user's local timezone
 * @param isoDateString - ISO format date string (e.g., "2024-03-15" or "2024-03-15T00:00:00Z")
 * @returns Date object set to UTC date (not shifted to local timezone)
 */
export const parseUTCDate = (isoDateString: string | null | undefined): Date | null => {
  if (!isoDateString) return null;
  
  try {
    // Extract year, month, day from ISO string
    const parts = isoDateString.split('T')[0].split('-');
    if (parts.length !== 3) return null;
    
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const day = parseInt(parts[2], 10);
    
    // Create date in UTC without timezone conversion
    return new Date(year, month, day);
  } catch (e) {
    console.error('Error parsing UTC date:', e);
    return null;
  }
};

/**
 * Format Date object to display string (MM/DD/YYYY) WITHOUT timezone conversion
 * This ensures displayed dates remain consistent regardless of user's local timezone
 * @param date - Date object to format
 * @returns Formatted date string (MM/DD/YYYY) or '--' if null
 */
export const formatDateForDisplay = (date: Date | null | undefined): string => {
  if (!date) return '--';
  
  try {
    // Use local date components (which are already in the correct timezone from parseUTCDate)
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}/${day}/${year}`;
  } catch (e) {
    console.error('Error formatting date for display:', e);
    return '--';
  }
};

/**
 * Format date to YYYY-MM-DD string for API
 */
export const formatDateForApi = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get first day of month
 */
export const getFirstDayOfMonth = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

/**
 * Get last day of month
 */
export const getLastDayOfMonth = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

/**
 * Get first day of week (Sunday)
 */
export const getFirstDayOfWeek = (date: Date): Date => {
  const d = new Date(date);
  const dayOfWeek = d.getDay();
  const diff = d.getDate() - dayOfWeek;
  return new Date(d.setDate(diff));
};

/**
 * Get last day of week (Saturday)
 */
export const getLastDayOfWeek = (date: Date): Date => {
  const d = new Date(date);
  const dayOfWeek = d.getDay();
  const diff = d.getDate() - dayOfWeek + 6;
  return new Date(d.setDate(diff));
};
